import { Link } from 'react-router-dom';
import { useAuth } from '../../providers/AuthContext';
import { useNetworkData } from '../../hooks/network/useNetworkData';
import { useQuery } from '@tanstack/react-query';
import api from '../../API';
import { ADMIN_SETUP_STEPS, DATA_MANAGER_HOME, DATA_MANAGER_WORKSPACE_LABEL } from '../../constants/setupRoadmapSteps';
import styles from './SetupRoadmap.module.css';

function SetupRoadmap() {
    const { girderInfo, oauth } = useAuth();
    const { local, isLoading: networkLoading, connectedList } = useNetworkData();

    const hasCilogon = Array.isArray(oauth) && oauth.some(
        (p) => (p.name && p.name.toLowerCase().includes('cilogon')) || (p.id && String(p.id).toLowerCase() === 'cilogon')
    );

    const { data: agentsResponse, isLoading: agentsLoading } = useQuery({
        queryKey: ['agents-list'],
        queryFn: () => api.get('agent', { query: { activeOnly: false } }),
        enabled: !!local,
        staleTime: 5 * 60 * 1000,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
    });

    const { data: collectionsResponse, isLoading: collectionsLoading } = useQuery({
        queryKey: ['collections-list'],
        queryFn: () => api.get('agent/rag/collections'),
        enabled: !!local,
        staleTime: 5 * 60 * 1000,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
    });

    const isLoading = networkLoading || agentsLoading || collectionsLoading;

    const isInitialConfigComplete = (localDoc) => {
        if (!localDoc) return false;
        const hasSiteName = localDoc.siteName && localDoc.siteName.trim() !== '';
        const hasApiUrl = localDoc.apiUrl && localDoc.apiUrl.trim() !== '';
        if (!hasSiteName || !hasApiUrl) return false;
        if (localDoc.isHub || false) {
            const hasNetworkName = localDoc.networkName && localDoc.networkName.trim() !== '';
            if (!hasNetworkName) return false;
        }
        return true;
    };

    const isConfigured = !!local;
    const serverConfigured = girderInfo && girderInfo !== 'pending' && typeof girderInfo === 'object' && girderInfo.isConfigured === true;
    const isInitialComplete = isInitialConfigComplete(local);
    const hasConnections = connectedList && connectedList.length > 0;

    const hasAgents = agentsResponse?.status === 200 &&
                     agentsResponse?.data?.data &&
                     agentsResponse.data.data.length > 0;

    const hasCollections = collectionsResponse?.status === 200 &&
                          collectionsResponse?.data?.collections &&
                          collectionsResponse.data.collections.length > 0;

    const completionByStep = {
        initial: isInitialComplete,
        network: hasConnections,
        agents: hasAgents || hasCollections,
        cilogon: hasCilogon,
    };
    const canAccessByStep = {
        initial: true,
        network: serverConfigured && isInitialComplete,
        agents: serverConfigured && isInitialComplete,
        cilogon: serverConfigured && isInitialComplete,
    };
    const isRequiredByStep = { initial: true, network: false, agents: false, cilogon: false };

    const steps = ADMIN_SETUP_STEPS.map((step) => ({
        ...step,
        isComplete: completionByStep[step.id] ?? false,
        isRequired: isRequiredByStep[step.id] ?? false,
        canAccess: canAccessByStep[step.id] ?? false,
        hasRecord: step.id === 'initial' ? isConfigured : undefined,
    }));

    if (isLoading) {
        return <div>Loading...</div>;
    }

    return (
        <div className={styles.container}>
            <h1>BDSA Server Setup Roadmap</h1>
            <p className={styles.intro}>
                Follow these steps to configure your BDSA server. Complete the initial server configuration
                before proceeding to other steps. After that, you can complete the remaining steps in any order.
                Stain, region, protocol, and Case Builder workflows live in the{' '}
                <Link to={DATA_MANAGER_HOME}>{DATA_MANAGER_WORKSPACE_LABEL}</Link>.
            </p>

            <div className={styles.stepsGrid}>
                {steps.map((step) => {
                    let statusClass, statusIcon, statusText;
                    if (!step.canAccess) {
                        statusClass = styles.locked;
                        statusIcon = '🔒';
                        statusText = 'Locked';
                    } else if (step.id === 'initial') {
                        if (step.isComplete) {
                            statusClass = styles.complete;
                            statusIcon = '✓';
                            statusText = 'Complete';
                        } else if (step.hasRecord) {
                            statusClass = styles.incomplete;
                            statusIcon = '⚠';
                            statusText = 'Incomplete';
                        } else {
                            statusClass = styles.inProgress;
                            statusIcon = '○';
                            statusText = 'Not Started';
                        }
                    } else if (step.isComplete) {
                        statusClass = styles.complete;
                        statusIcon = '✓';
                        statusText = 'Complete';
                    } else {
                        statusClass = styles.inProgress;
                        statusIcon = '○';
                        statusText = 'In Progress';
                    }

                    return (
                        <div
                            key={step.id}
                            className={`${styles.stepCard} ${statusClass}`}
                        >
                            <div className={styles.stepHeader}>
                                <div className={styles.stepNumber}>{step.number}</div>
                                <div className={styles.stepInfo}>
                                    <h2 className={styles.stepTitle}>{step.title}</h2>
                                    <div className={styles.stepStatus}>
                                        <span className={styles.statusIcon}>{statusIcon}</span>
                                        <span className={styles.statusText}>{statusText}</span>
                                    </div>
                                </div>
                            </div>

                            <p className={styles.stepDescription}>{step.description}</p>

                            {step.canAccess ? (
                                <Link
                                    to={step.route}
                                    className={styles.stepLink}
                                >
                                    {step.isComplete ? 'Review Configuration' : 'Configure'}
                                </Link>
                            ) : (
                                <div className={styles.stepLinkDisabled}>
                                    Complete initial setup to unlock
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {!isInitialComplete && (
                <div className={styles.warningBox}>
                    <strong>Get Started:</strong> Begin by completing the Initial Server Configuration step above.
                    {isConfigured && !isInitialComplete && ' Some required fields are missing.'}
                </div>
            )}
        </div>
    );
}

export default SetupRoadmap;
